import React, { useState } from 'react';
import Container from '../../components/container/Container';
import { exitApp } from '../../hooks/utils';
import CountDown from '../../components/countdown/Countdown';
import { mutationKeys, useSendOtpMutation, useValidateOtpMutation } from '../../hooks/mutationHooks';
import { useMutationState } from '@tanstack/react-query';
import SplitInputOtp from '../../components/splitInputOtp/SplitInputOtp';
import InfoBox from '../../components/infoBox/InfoBox';
import FeatherIcon from 'feather-icons-react';
import { useSettings } from '../../hooks/queryHooks';
import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';

const OtpScreen = () => {
  const history = useHistory();
  const { t } = useTranslation();
  const [otpCustomError, setOtpCustomError] = useState<string>();
  const [btnState, setBtnState] = useState({
    disabled: true,
    resetCount: 0
  });
  const { settings } = useSettings();
  const { mutateAsync, isPending, data: sendOtpData } = useSendOtpMutation();
  const {
    mutateAsync: validateOtp,
    isPending: isValidating,
    error,
    isError,
    data: validateOtpData
  } = useValidateOtpMutation();

  const [{ otpTimeoutSeconds, status } = {}] = useMutationState({
    filters: { mutationKey: [mutationKeys.sendOtpMutation] },
    select: mutation => mutation.state.data
  }) as Array<ReturnType<typeof useSendOtpMutation>['data']>;

  const primaryBtnText =
    validateOtpData?.status === 'Failed' ? (
      'Go to bank selection'
    ) : otpTimeoutSeconds && status === 'Sent' ? (
      <CountDown key={btnState.resetCount} maxSeconds={otpTimeoutSeconds} setBtnState={setBtnState} />
    ) : (
      'Sending...'
    );

  const primaryBtnClick = async () => {
    if (validateOtpData?.status === 'Failed') {
      history.push('bank-select');
      return;
    }
    await mutateAsync();
    setOtpCustomError('');
    setBtnState(prev => ({
      ...prev,
      disabled: true,
      resetCount: prev.resetCount + 1
    }));
  };

  return (
    <Container
      primaryBtn={{
        text: primaryBtnText,
        onClick: primaryBtnClick,
        disabled: isPending || btnState.disabled
      }}
      {...(settings?.redirectUrl && {
        secondaryBtn: {
          text: t('Abort & return to partner'),
          onClick: () => exitApp()
        }
      })}
    >
      <h1 className="uppercase text-md sm:text-lg mb-2 font-semi-bold text-primary-2">Code sent</h1>
      <p className="text-base text-primary-2">Please enter the 6-digit code we sent you below.</p>
      <SplitInputOtp
        key={`split-input-${btnState.resetCount}`}
        validateOtp={validateOtp}
        setOtpCustomError={setOtpCustomError}
        setBtnState={setBtnState}
        otpCustomError={otpCustomError}
        isValidating={isValidating}
      />
      <p aria-live="polite" aria-atomic="true" className="text-negative mt-4 sm:mt-6">
        {(otpCustomError || isError || sendOtpData?.status === 'Failed') &&
          (error?.message || otpCustomError || sendOtpData?.message)}
        <br />
        {validateOtpData?.status === 'Failed' && (
          <InfoBox icon={<FeatherIcon icon="alert-circle" className="text-gray-500" />} className="mt-2">
            <div className="mt-4 font-normal text-wrap leading-6">
              We have been unable to verify your identity with TransUnion. Don't worry, we can still proceed with
              connecting your bank account with {settings?.customerName}. Simply click on the "Go to bank selection"
              button below.
            </div>
          </InfoBox>
        )}
      </p>
    </Container>
  );
};

export default OtpScreen;
